import { BaseTable } from "@/components/shared/data-table";
import { ServerSideDataTablePagination } from "@/components/shared/tables/server-side-table-pagination";
import { useNavigateToInvoiceWithBrowsingQueue } from "@/hooks/useNavigateToInvoiceWithBrowsingQueue";
import type { RawFilters } from "@/hooks/useQueryFilters";
import type { GetAllInvoiceInvoice } from "@/queries/invoices/get-all";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useRef } from "react";
import { useInvoicesForTable } from "../use-invoices-for-table";
import { InvoicesFilters } from "./invoices-filters";
import { TableActionBar } from "./invoices-table-action-bar";

type Props = {
  storageKey: string;
  columns: ColumnDef<GetAllInvoiceInvoice>[];
  onDelete: (invoiceIds: string[]) => void;
  onConfirm: (invoiceIds: string[]) => void;
  onPaid: (invoiceIds: string[]) => void;
  tab: InvoicesTab;
  emptyStateComponent?: React.ReactNode;
  guidingMessage?: string;
  defaultFilters: RawFilters<any>;
};

export enum InvoicesTab {
  TO_REVIEW = "to_review",
  REVIEWED = "reviewed",
  ALL = "all",
}

export const InvoicesDataTable = ({
  storageKey,
  columns,
  onDelete,
  onConfirm,
  onPaid,
  tab,
  emptyStateComponent,
  guidingMessage,
  defaultFilters,
}: Props) => {
  const tableRef = useRef(null);

  const {
    table,
    loading,
    pagination,
    filters,
    setFilters,
    globalSearch,
    setGlobalSearch,
    clickableColumns,
    columnVisibility,
    data,
    stickyColumns,
    footer,
    setPage,
    setLimit,
  } = useInvoicesForTable({ storageKey, defaultFilters, columns });

  const navigateToInvoiceWithBrowsingQueue =
    useNavigateToInvoiceWithBrowsingQueue(table);

  const handleRowClick = useCallback(
    (_event: React.MouseEvent, row: Row<any>) => {
      navigateToInvoiceWithBrowsingQueue(row.id);
    },
    [navigateToInvoiceWithBrowsingQueue],
  );

  return (
    <div className="flex flex-col gap-2 h-full">
      {guidingMessage && (
        <p className="text-sm text-gray-600 mt-2 font-medium">
          {guidingMessage}
        </p>
      )}
      <InvoicesFilters
        showStartReviewButton={tab === InvoicesTab.TO_REVIEW}
        filters={filters}
        setFilters={setFilters}
        globalSearch={globalSearch}
        setGlobalSearch={setGlobalSearch}
        table={table}
      />
      <BaseTable
        table={table}
        clickableColumns={clickableColumns}
        columnVisibility={columnVisibility}
        columns={columns}
        data={data}
        stickyColumns={stickyColumns}
        footer={footer}
        loading={loading}
        ref={tableRef}
        onRowClick={handleRowClick}
        emptyStateComponent={emptyStateComponent}
      />
      <div className="mt-0.5" />
      <ServerSideDataTablePagination
        table={table}
        totalItems={pagination.totalItems}
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        pageSize={pagination.itemsPerPage}
        onPageChange={setPage}
        onPageSizeChange={setLimit}
      />
      <TableActionBar
        table={table}
        onDelete={onDelete}
        onConfirm={onConfirm}
        onPaid={onPaid}
        currentTab={tab}
      />
    </div>
  );
};
